import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { Document } from '../../types'
import { formatDate } from '../../utils/dateFormatter'

interface DocumentLinkInputProps {
  value: number[] // Array of document IDs
  onChange: (value: number[]) => void
  disabled?: boolean
  parentDocumentId?: number
}

export const DocumentLinkInput: React.FC<DocumentLinkInputProps> = ({ value = [], onChange, disabled = false, parentDocumentId }) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([])
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Load selected documents by IDs using getFew method (like React)
  const loadSelectedDocuments = useCallback(async (documentIds: number[]) => {
    if (documentIds.length === 0) {
      setSelectedDocuments([])
      return
    }

    try {
      const response = await documentService.getFew(documentIds, { fields: 'id,title,created' })

      // Map the IDs to documents, handling missing documents
      const documents = documentIds.map((id) => {
        const found = response.results.find((doc) => doc.id === id)
        if (found) {
          return found
        } else {
          // Create a minimal document object for missing documents
          return {
            id,
            title: '',
            content: '',
            created: '',
            added: '',
            modified: '',
            archive_serial_number: null,
            correspondent: null,
            document_type: null,
            workflow_status: null,
            storage_path: null,
            tags: [],
            owner: null,
            original_file_name: '',
            archived_file_name: '',
            mime_type: '',
            page_count: 0,
            notes: [],
            custom_fields: [],
            is_shared_by_requester: false,
            version_number: 0,
            version_group_title: '',
          } as Document
        }
      })
      setSelectedDocuments(documents)
    } catch (error) {
      setSelectedDocuments([])
    }
  }, [])

  // Search for documents
  const searchDocuments = useCallback(
    async (term: string) => {
      if (!term || term.length < 2) {
        setSearchResults([])
        return
      }

      setLoading(true)
      try {
        const response = await documentService.listFiltered(
          1,
          10, // Limit to 10 results
          'created',
          true,
          [{ rule_type: 0, value: term }], // FILTER_TITLE
          { truncate_content: true }
        )

        // Filter out current document and already selected documents
        const filtered = response.results.filter((doc) => doc.id !== parentDocumentId && !value.includes(doc.id))

        setSearchResults(filtered)
      } catch (error) {
        setSearchResults([])
      } finally {
        setLoading(false)
      }
    },
    [parentDocumentId, value]
  )

  // Load selected documents when value changes
  useEffect(() => {
    loadSelectedDocuments(value)
  }, [value, loadSelectedDocuments])

  // Search when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDocuments(searchTerm)
    }, 300) // Debounce search

    return () => clearTimeout(timeoutId)
  }, [searchTerm, searchDocuments])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleAddDocument = (document: Document) => {
    const newValue = [...value, document.id]
    onChange(newValue)
    setSearchTerm('')
    setShowDropdown(false)
  }

  const handleRemoveDocument = (documentId: number) => {
    const newValue = value.filter((id) => id !== documentId)
    onChange(newValue)
  }

  return (
    <div className="document-link-input">
      <div className="position-relative">
        {/* Combined Input Field with Selected Documents and Search */}
        <div
          className="form-control d-flex flex-wrap align-items-center gap-1"
          style={{
            minHeight: '38px',
            padding: '0.375rem 0.75rem',
            cursor: disabled ? 'not-allowed' : 'text',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
          onClick={() => !disabled && searchInputRef.current?.focus()}
        >
          {/* Selected Documents as Chips */}
          {selectedDocuments.map((doc) => (
            <div key={doc.id} className="d-flex align-items-center" style={{ maxWidth: '200px', flexShrink: 0 }}>
              {!disabled && (
                <button
                  type="button"
                  className="btn p-0 lh-1 me-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveDocument(doc.id)
                  }}
                  title={t('remove_link')}
                  style={{ fontSize: '0.8em', color: '#dc3545' }}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
              {doc.title ? (
                <a
                  href={`/documents/${doc.id}`}
                  className="badge bg-light text-primary d-flex align-items-center"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('open_link')}
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <i className="bi bi-file-text me-1" style={{ fontSize: '0.9em' }}></i>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.title}</span>
                </a>
              ) : (
                <span className="badge bg-light text-muted d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-1" style={{ fontSize: '0.9em' }}></i>
                  <span>Not found</span>
                </span>
              )}
            </div>
          ))}

          {/* Search Input (invisible, just for typing) */}
          {!disabled && (
            <input
              ref={searchInputRef}
              type="text"
              placeholder={selectedDocuments.length === 0 ? 'Search for documents...' : ''}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                flex: 1,
                minWidth: '150px',
              }}
            />
          )}
        </div>

        {/* Dropdown with Search Results */}
        {showDropdown && !disabled && (
          <div ref={dropdownRef} className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, top: '100%' }}>
            {loading && (
              <div className="p-2 text-muted">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Searching...
                </div>
              </div>
            )}
            {!loading && searchResults.length === 0 && searchTerm.length >= 2 && <div className="p-2 text-muted">No documents found</div>}
            {searchResults.map((doc) => (
              <div
                key={doc.id}
                className="p-2 border-bottom cursor-pointer"
                onClick={() => handleAddDocument(doc)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-file-text me-2" style={{ fontSize: '0.9em' }}></i>
                  <div>
                    <div>{doc.title || `Document ${doc.id}`}</div>
                    <small className="text-muted">{formatDate(doc.created || '')}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
